/**
 * @architecture-header 1
 * @layer module-infrastructure
 * @domain settings-preferences
 * @responsibility Own the backend-authoritative SET-F03 preferences query and its update command,
 *   and reflect a successful update into the shared workspace language store immediately.
 * @state-owner TanStack Query; shared workspace language store on a successful mutation
 * @allowed-imports app environment/session; auth module public API; same-module API and contracts;
 *   shared workspace language store; TanStack Query
 * @forbidden component UI; fabricated preferences data; direct Supabase calls; name/avatar fields
 * @public-api settings-preferences hooks
 * @side-effects authenticated preferences read/write, cache update, and immediate UI language switch
 * @tests frontend/packages/workspace-web/src/modules/settings-preferences/components/PreferencesSurface.test.tsx
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Locale, PreferencesContext } from '@vispo/contracts';
import { useOptionalWorkspaceEnvironment } from '../../../app/config/environment';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { loadMeContext } from '../../auth';
import { updatePreferences } from './settings-preferences';

export const settingsPreferencesKeys = {
  preferences: (userId: string) => ['set-f03', 'preferences', userId] as const,
};

export function usePreferences() {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const userId = session?.userId ?? 'anonymous';
  return useQuery({
    queryKey: settingsPreferencesKeys.preferences(userId),
    enabled: Boolean(environment && (session || environment.demoMode)),
    // Demo data is a locally-mutated seed, not server state: an automatic background
    // refetch (window focus, reconnect) would silently discard an in-flight edit.
    staleTime: environment?.demoMode ? Number.POSITIVE_INFINITY : undefined,
    queryFn: async (): Promise<PreferencesContext> => {
      if (environment?.demoMode) {
        const e2e = globalThis as typeof globalThis & {
          __VISPO_SET_F03_PREFERENCES_LOAD_DEFERRED__?: boolean;
          __VISPO_SET_F03_PREFERENCES_LOAD_RESOLVE__?: () => void;
        };
        if (e2e.__VISPO_SET_F03_PREFERENCES_LOAD_DEFERRED__) {
          await new Promise<void>((resolve) => {
            e2e.__VISPO_SET_F03_PREFERENCES_LOAD_RESOLVE__ = resolve;
          });
          delete e2e.__VISPO_SET_F03_PREFERENCES_LOAD_DEFERRED__;
          delete e2e.__VISPO_SET_F03_PREFERENCES_LOAD_RESOLVE__;
        }
        return { locale: useWorkspaceUiStore.getState().language };
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const context = await loadMeContext(environment.apiClient);
      if (!context.preferences) throw new Error('偏好設定尚未建立');
      return context.preferences;
    },
  });
}

export function useUpdatePreferences() {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const userId = session?.userId ?? 'anonymous';
  const queryClient = useQueryClient();
  const queryKey = settingsPreferencesKeys.preferences(userId);

  return useMutation({
    mutationFn: async (locale: Locale): Promise<PreferencesContext> => {
      if (environment?.demoMode) return { locale };
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const response = await updatePreferences(environment.apiClient, {
        locale,
        idempotencyKey: `agent-a-set-f03-${crypto.randomUUID()}`,
      });
      return response.preferences;
    },
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKey, preferences);
      useWorkspaceUiStore.getState().setLanguage(preferences.locale);
    },
  });
}
